import { useEffect, useRef } from 'react';

// Nyckel-prefix i sessionStorage. Vi sparar window-scrollY per "scope" (t.ex.
// per shoot) så användaren landar på samma position när de går tillbaka från
// detail-vyn.
const STORAGE_PREFIX = 'loupe:scroll:';
// Separat prefix för "senast sedd bild" — används av Detail för att uppdatera
// var Back-knappen ska scrolla till när användaren navigerat J/K i detail.
const FOCUS_PREFIX = 'loupe:focus:';

interface SavedScroll {
  // Pixel-position vid bortnavigering. Används som fallback om DOM-bilden
  // inte hittas vid restore.
  y: number;
  // ID på den bild användaren senast hade fokus på. Vi föredrar att scrolla
  // den i view (mer robust än pixlar när layout kan skifta något).
  imageId?: string;
}

function scrollKey(scope: string): string {
  return `${STORAGE_PREFIX}${scope}`;
}

function focusKey(scope: string): string {
  return `${FOCUS_PREFIX}${scope}`;
}

// Sparar nuvarande window.scrollY + ev. fokusbild för det angivna scopet.
// Anropas innan navigering bort från sidan (t.ex. när man klickar på en bild).
export function saveScrollPosition(scope: string, imageId?: string): void {
  try {
    const payload: SavedScroll = { y: window.scrollY, imageId };
    sessionStorage.setItem(scrollKey(scope), JSON.stringify(payload));
  } catch {
    // sessionStorage kan vara blockerad i privat läge — ignorera tyst.
  }
}

// Sätter "senast sedd bild" för scope. Detail anropar detta varje gång
// användaren navigerar J/K så Back hamnar på rätt frame.
export function setLastFocusedImage(scope: string, imageId: string): void {
  try {
    sessionStorage.setItem(focusKey(scope), imageId);
  } catch {
    /* ignore */
  }
}

// Rensar sparad scroll-position. Användbart t.ex. när användaren klickar
// "Shoots" och förväntar sig att börja från toppen.
export function clearScrollPosition(scope: string): void {
  try {
    sessionStorage.removeItem(scrollKey(scope));
    sessionStorage.removeItem(focusKey(scope));
  } catch {
    /* ignore */
  }
}

// Rensar ALLA loupe-scroll-positioner. Anropas på shoot-listan för att hålla
// sessionStorage rent och säkerställa att man börjar från toppen i nya shoots.
export function clearAllScrollPositions(): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && (k.startsWith(STORAGE_PREFIX) || k.startsWith(FOCUS_PREFIX))) {
        toRemove.push(k);
      }
    }
    toRemove.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

interface UseScrollRestorationArgs {
  // Unik nyckel per "vy" — t.ex. shootId. Ändras nyckeln är det ny vy och vi
  // restoreas med ny nyckel.
  scope: string | undefined;
  // True när innehållet är klart att scrolla till (oftast: bilder laddade).
  // Vi väntar på detta INNAN restore eftersom dokumentets höjd måste finnas.
  ready: boolean;
  // Selector-funktion som returnerar DOM-noden för en given imageId. Om vi
  // har ett "senast fokuserad bild"-ID i sessionStorage scrollar vi det
  // elementet centerat i view istället för att använda råa pixlar — det är
  // mer robust när layout kan ha skiftat något (lazy loading, etc.).
  getElementForImage?: (imageId: string) => HTMLElement | null;
}

// Hook som restorear scroll-pos EN gång per (scope, ready=true). Strategi:
//   1) Kolla efter "senast fokuserad bild" (uppdateras av Detail vid J/K).
//   2) Om hittad och getElementForImage returnerar en nod → scrollIntoView.
//   3) Annars fallback till sparad pixel-position.
// Använder requestAnimationFrame x 2 för att vänta in layout-pass efter render.
export function useScrollRestoration({
  scope,
  ready,
  getElementForImage,
}: UseScrollRestorationArgs): void {
  // Säkerställer att vi bara restorear en gång per scope-byte.
  const restoredFor = useRef<string | null>(null);

  useEffect(() => {
    if (!scope || !ready) return;
    if (restoredFor.current === scope) return;

    const focusedId = sessionStorage.getItem(focusKey(scope));
    const savedRaw = sessionStorage.getItem(scrollKey(scope));

    let savedY: number | null = null;
    if (savedRaw) {
      try {
        const parsed = JSON.parse(savedRaw) as SavedScroll;
        if (Number.isFinite(parsed.y)) savedY = parsed.y;
      } catch {
        // Bakåtkompatibilitet: gammalt format kunde vara plain "123".
        const n = parseInt(savedRaw, 10);
        if (Number.isFinite(n)) savedY = n;
      }
    }

    if (focusedId == null && savedY == null) {
      restoredFor.current = scope;
      return;
    }

    // Två rAF-tics: en för React commit, en för browserns layout-pass. Då har
    // bilderna fått sin aspect-ratio-baserade höjd och dokumentet är högt nog.
    let id2 = 0;
    const id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => {
        const el = focusedId && getElementForImage ? getElementForImage(focusedId) : null;
        if (el) {
          // Centrera bilden i viewport — matchar känslan "där jag var".
          const rect = el.getBoundingClientRect();
          const targetY = window.scrollY + rect.top - window.innerHeight / 2 + rect.height / 2;
          window.scrollTo({ top: Math.max(0, targetY), behavior: 'auto' });
        } else if (savedY != null) {
          window.scrollTo({ top: savedY, behavior: 'auto' });
        }
        restoredFor.current = scope;
      });
    });

    return () => {
      cancelAnimationFrame(id1);
      if (id2) cancelAnimationFrame(id2);
    };
  }, [scope, ready, getElementForImage]);
}
